import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ChevronDown, MapPin } from "lucide-react";
import { useState } from "react";
import { RANGPUR_AREAS, type RangpurAreaMeta } from "@/content/rangpur-areas";

type Props = {
  current: { slug: string; name: string; geo: { lat: number; lng: number } };
};

/**
 * Consistent hub → area → nearby navigation widget rendered on every
 * Rangpur area page. Combines:
 *   - Breadcrumb-style trail (Rangpur hub → current area)
 *   - Prev / next area pagination (alphabetical, deterministic)
 *   - 3 nearest areas by haversine distance
 *   - "Jump to area" dropdown listing every Rangpur area
 */
export function RangpurAreaNav({ current }: Props) {
  const [open, setOpen] = useState(false);

  const sorted = [...RANGPUR_AREAS].sort((a, b) => a.name.localeCompare(b.name));
  const idx = sorted.findIndex((a) => a.slug === current.slug);
  const prev = sorted[(idx - 1 + sorted.length) % sorted.length];
  const next = sorted[(idx + 1) % sorted.length];

  const toRad = (n: number) => (n * Math.PI) / 180;
  const distanceKm = (b: RangpurAreaMeta) => {
    const R = 6371;
    const dLat = toRad(b.geo.lat - current.geo.lat);
    const dLng = toRad(b.geo.lng - current.geo.lng);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(current.geo.lat)) * Math.cos(toRad(b.geo.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };
  const nearest = RANGPUR_AREAS
    .filter((a) => a.slug !== current.slug)
    .map((a) => ({ area: a, km: distanceKm(a) }))
    .sort((x, y) => x.km - y.km)
    .slice(0, 3);

  return (
    <nav
      aria-label="Rangpur area navigation"
      className="container-x mt-6"
    >
      <div className="rounded-3xl border border-border bg-card/70 backdrop-blur p-5 md:p-6">
        {/* Trail */}
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          <li>
            <Link to="/seo-locations" className="hover:text-foreground">All locations</Link>
          </li>
          <li aria-hidden>›</li>
          <li>
            <Link to="/seo-services/$location" params={{ location: "rangpur" }} className="hover:text-foreground">
              Rangpur hub
            </Link>
          </li>
          <li aria-hidden>›</li>
          <li className="text-foreground normal-case tracking-normal text-sm font-medium flex items-center gap-1.5">
            <MapPin size={12} className="text-primary" /> {current.name}
          </li>
        </ol>

        <div className="mt-5 grid gap-4 md:grid-cols-12 md:items-center">
          {/* Prev / next */}
          <div className="md:col-span-5 grid grid-cols-2 gap-2">
            <Link
              to="/seo-services/rangpur/$area"
              params={{ area: prev.slug }}
              className="group rounded-2xl border border-border bg-background px-4 py-3 hover:border-primary transition"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <ArrowLeft size={11} /> Previous
              </div>
              <div className="mt-1 text-sm font-medium truncate">{prev.name}</div>
            </Link>
            <Link
              to="/seo-services/rangpur/$area"
              params={{ area: next.slug }}
              className="group rounded-2xl border border-border bg-background px-4 py-3 hover:border-primary transition text-right"
            >
              <div className="flex items-center justify-end gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Next <ArrowRight size={11} />
              </div>
              <div className="mt-1 text-sm font-medium truncate">{next.name}</div>
            </Link>
          </div>

          {/* Nearest 3 */}
          <ul className="md:col-span-4 flex flex-wrap gap-2">
            {nearest.map(({ area, km }) => (
              <li key={area.slug}>
                <Link
                  to="/seo-services/rangpur/$area"
                  params={{ area: area.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:border-primary hover:text-foreground text-muted-foreground transition"
                >
                  <MapPin size={11} className="text-primary" />
                  {area.name}
                  <span className="text-[10px] opacity-70">· {km.toFixed(1)}km</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Jump to area */}
          <div className="md:col-span-3 relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-haspopup="listbox"
              className="w-full inline-flex items-center justify-between gap-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm hover:border-primary transition"
            >
              Jump to area
              <ChevronDown size={14} className={open ? "rotate-180 transition" : "transition"} />
            </button>
            {open && (
              <ul
                role="listbox"
                className="absolute right-0 z-30 mt-2 max-h-72 w-full min-w-[16rem] overflow-auto rounded-2xl border border-border bg-popover shadow-lg p-2"
              >
                {sorted.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to="/seo-services/rangpur/$area"
                      params={{ area: a.slug }}
                      onClick={() => setOpen(false)}
                      className={`block rounded-lg px-3 py-2 text-sm hover:bg-card ${a.slug === current.slug ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      {a.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}